import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { useAuth } from "@/utils/auth";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export interface MissingProfileItemT {
    key: "organisation" | "name" | "email";
    title: string;
    message: string;
    actionLabel: string;
    to: "/profile/organisation" | "/profile/name" | "/profile/email";
}

/**
 * Profile setup items a staff member still needs to fill in. Each one counts towards the red
 * Profile nav badge and shows as its own notice on the profile page.
 */
export default function useMissingProfileItems() {
    const auth = useAuth();
    const { data: profile, isLoading } = useQuery(selectProfileByIdQueryOptions(auth.id));

    const items = useMemo(() => {
        const missing: MissingProfileItemT[] = [];
        if (!profile?.organisations?.length) {
            missing.push({
                key: "organisation",
                title: "Organisation missing",
                message: "Join an organisation to create diaries and invite patients.",
                actionLabel: "Add organisation",
                to: "/profile/organisation",
            });
        }
        if (!profile?.title || !profile?.first_name || !profile?.last_name) {
            missing.push({
                key: "name",
                title: "Name missing",
                message: "Add your title, first name and last name.",
                actionLabel: "Add name",
                to: "/profile/name",
            });
        }
        if (!profile?.email) {
            missing.push({
                key: "email",
                title: "Email missing",
                message: "Add the email address you use to sign in.",
                actionLabel: "Add email",
                to: "/profile/email",
            });
        }
        return missing;
    }, [profile?.email, profile?.first_name, profile?.last_name, profile?.organisations?.length, profile?.title]);

    return { items, isLoading };
}
